import type { StepApplyResult, TransformRow, UnpivotConfig } from './types';

/**
 * Wide rows become long rows: each listed metric column is one output row
 * with a label and a value, and every other column is copied onto it.
 *
 * A missing source column skips the step. Emitting a partial unpivot would
 * look like the metric was zero.
 */
export function unpivotWideToLong(
  rows: readonly TransformRow[],
  config: UnpivotConfig,
): StepApplyResult {
  const missing = config.columns.filter((column) =>
    rows.some((row) => !Object.prototype.hasOwnProperty.call(row, column)),
  );
  if (missing.length > 0) {
    return {
      rows: [...rows],
      warnings: [
        `unpivot step skipped: missing column(s) ${missing.join(', ')}`,
      ],
    };
  }

  const metricCols = new Set(config.columns);
  const result: TransformRow[] = [];

  for (const row of rows) {
    const carried: TransformRow = {};
    for (const [key, value] of Object.entries(row)) {
      if (!metricCols.has(key)) carried[key] = value;
    }

    for (let i = 0; i < config.columns.length; i++) {
      const source = config.columns[i];
      const value = row[source];
      result.push({
        ...carried,
        [config.labelKey]: config.labels[i] ?? source,
        [config.valueKey]: value ?? 0,
      });
    }
  }

  return { rows: result, warnings: [] };
}
